import { useState } from "react";
import { createPortal } from "react-dom";
import { ExternalLink, Copy, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";

// True inside social in-app browsers (FB/IG/Messenger/TikTok/etc.) that break
// video playback, install-to-home and push notifications.
function isInAppBrowser() {
  try {
    const ua = navigator.userAgent || "";
    return /FBAN|FBAV|FB_IAB|FBIOS|Instagram|Messenger|WhatsApp|Line\/|Twitter|TikTok|musical_ly|Snapchat|Pinterest|LinkedInApp/i.test(ua);
  } catch { return false; }
}

export default function OpenInBrowserBanner() {
  const { t } = useLang();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("nicabros_openbrowser_dismissed") === "1"; } catch { return false; }
  });

  if (!isInAppBrowser() || dismissed) return null;

  const url = typeof window !== "undefined" ? window.location.href : "";
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);

  const dismiss = () => {
    try { sessionStorage.setItem("nicabros_openbrowser_dismissed", "1"); } catch (e) {}
    setDismissed(true);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("linkCopied"));
    } catch (e) {
      // Fallback: legacy execCommand
      try {
        const ta = document.createElement("textarea");
        ta.value = url; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success(t("linkCopied"));
      } catch (e2) { toast.error(url); }
    }
  };

  const openExternal = () => {
    if (isAndroid) {
      // Android intent: force-open in Chrome.
      const clean = url.replace(/^https?:\/\//, "");
      window.location.href = `intent://${clean}#Intent;scheme=https;package=com.android.chrome;end`;
      return;
    }
    if (isIOS) {
      // iOS can't force Safari — copy + instruct.
      copyLink();
      toast.message(t("iosHint"), { duration: 6000 });
      return;
    }
    window.open(url, "_blank");
  };

  const banner = (
    <div
      className="fixed top-0 left-0 right-0 z-[2147483647] bg-[#0067C6] text-white shadow-[0_4px_18px_rgba(0,0,0,0.5)]"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
      data-testid="open-in-browser-banner"
    >
      <div className="flex items-center gap-2 px-3 py-2.5 max-w-2xl mx-auto">
        <ExternalLink size={20} strokeWidth={2.5} className="shrink-0" />
        <p className="font-vt text-[13px] leading-tight flex-1">{t("openInBrowserMsg")}</p>
        <button
          onClick={openExternal}
          data-testid="open-in-browser-btn"
          className="shrink-0 bg-white text-[#0067C6] font-extrabold text-[12px] rounded-lg px-3 py-2 flex items-center gap-1.5 active:scale-95 transition-transform"
        >
          {isIOS ? <Copy size={14} strokeWidth={3} /> : <ExternalLink size={14} strokeWidth={3} />}
          {isIOS ? t("copyLink") : t("openInBrowserBtn")}
        </button>
        <button onClick={dismiss} data-testid="open-in-browser-dismiss" aria-label="Dismiss" className="shrink-0 text-white/80 hover:text-white p-1">
          <X size={18} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );

  return createPortal(banner, document.body);
}
